import React, { useEffect, useState, useRef } from "react";
import Chart from "react-apexcharts";
import { useFilter } from "../../../../FilterContext"; // Ensure the path is correct
import jsonData from "../../../../current-survey-numeric.json";

const D03HorizontalBarCharts = () => {
  const { filterCriteria } = useFilter();
  const containerRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(225);
  const [containerWidth, setContainerWidth] = useState(0);

  const [filteredScores1, setFilteredScores1] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores1, setUnfilteredScores1] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [filteredScores2, setFilteredScores2] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores2, setUnfilteredScores2] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  // Handle container resizing
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
        
        // Adjust chart height based on container width
        // For wider containers, we can have taller charts
        let newHeight = Math.min(225, width * 0.5);
        
        // Ensure minimum height
        newHeight = Math.max(newHeight, 160);
        
        setChartHeight(newHeight);
      }
    };

    // Initial update
    updateDimensions();
    
    // Add resize listener
    window.addEventListener('resize', updateDimensions);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const processScores = (data, question) => {
      const scores = {
        NR: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      };

      data.forEach((item) => {
        const score = item[question];
        if (
          score === 1 ||
          score === 2 ||
          score === 3 ||
          score === 4 ||
          score === 5
        ) {
          scores[score]++;
        } else {
          scores.NR++;
        }
      });

      return scores;
    };

    if (!jsonData || !Array.isArray(jsonData)) {
      console.error("jsonData is not loaded correctly or is not an array.");
      return;
    }

    if (!filterCriteria || typeof filterCriteria !== "object") {
      console.error("filterCriteria is not an object.");
      return;
    }

    // Filtered data includes all criteria
    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every((key) => {
        const criteria = filterCriteria[key];
        if (Array.isArray(criteria)) {
          return criteria.length === 0 || criteria.includes(item[key]);
        }
        return criteria === "" || item[key] === criteria;
      });
    });

    // Unfiltered except for survey_id
    const unfilteredExceptSurveyID = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every((key) => {
        const criteria = filterCriteria[key];
        if (key === "survey_id") {
          // Apply the survey_id filter
          if (Array.isArray(criteria)) {
            return criteria.length === 0 || criteria.includes(item[key]);
          }
          return criteria === "" || item[key] === criteria;
        }
        // Skip filtering for all other fields
        return true;
      });
    });

    const newFilteredScores1 = processScores(filteredData, "MQ0299");
    const newUnfilteredScores1 = processScores(
      unfilteredExceptSurveyID,
      "MQ0299"
    );

    setFilteredScores1(newFilteredScores1);
    setUnfilteredScores1(newUnfilteredScores1);

    const newFilteredScores2 = processScores(filteredData, "MQ0301");
    const newUnfilteredScores2 = processScores(
      unfilteredExceptSurveyID,
      "MQ0301"
    );

    setFilteredScores2(newFilteredScores2);
    setUnfilteredScores2(newUnfilteredScores2);
  }, [filterCriteria]);

  const createSeriesData = (filteredScores, unfilteredScores) => {
    return Object.keys(filteredScores).map((key) => {
      return {
        x: key,
        y: filteredScores[key],
        goals: [
          {
            name: "Unfiltered",
            value: unfilteredScores[key],
            strokeWidth: 5,
            strokeHeight: 10,
            strokeLineCap: "round",
            strokeColor: "#888888",
          },
        ],
      };
    });
  };

  const seriesData1 = createSeriesData(filteredScores1, unfilteredScores1);
  const seriesData2 = createSeriesData(filteredScores2, unfilteredScores2);

  // Calculate appropriate font size and label offset based on container width
  const fontSize = containerWidth < 400 ? '10px' : '12px';
  const labelOffset = containerWidth < 400 ? 40 : 70;

  const options = {
    chart: {
      type: "bar",
      toolbar: {
        show: false
      },
      fontFamily: 'Arial, sans-serif',
    },
    colors: ["#BAB0AC", "#F28E2B", "#F28E2B", "#F1CE63", "#499894", "#499894"],
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        horizontal: true,
        distributed: true,
        dataLabels: {
          position: "top",
        },
        barHeight: '60%', // Adjust bar height to fit better
      },
    },
    dataLabels: {
      enabled: true,
      offsetX: labelOffset,
      textAnchor: "end",
      style: {
        colors: ["#000"],
        fontSize: fontSize,
      },
      formatter: function (val, opt) {
        const goals =
          opt.w.config.series[opt.seriesIndex].data[opt.dataPointIndex].goals;
        if (goals && goals.length) {
          return `${parseFloat(val).toFixed(0)} / ${parseFloat(
            goals[0].value
          ).toFixed(0)}`;
        }
        return parseFloat(val).toFixed(0);
      },
    },
    xaxis: {
      categories: ["NR", "1", "2", "3", "4", "5"],
      labels: {
        style: {
          fontSize: fontSize,
        }
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: fontSize,
        }
      }
    },
    tooltip: {
      enabled: true,
      y: {
        title: "Score",
      },
    },
    legend: {
      show: false,
    },
    grid: {
      padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        dataLabels: {
          offsetX: 30,
        }
      }
    }]
  };

  // Style for titles to ensure they don't take too much space
  const titleStyle = {
    fontSize: fontSize,
    fontWeight: 'bold',
    marginBottom: '5px',
    marginTop: '10px'
  };

  // Wrapper style to ensure proper sizing
  const chartWrapperStyle = {
    width: '100%',
    height: `${chartHeight}px`,
    marginBottom: '10px'
  };

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
      <p style={titleStyle}>Self Reported Engagement</p>
      <div id="sre-bar" style={chartWrapperStyle}>
        <Chart
          options={options}
          series={[{ name: "Filtered", data: seriesData1 }]}
          type="bar"
          height="100%"
          width="100%"
        />
      </div>
      <p style={titleStyle}>Self Reported Retention</p>
      <div id="srr-bar" style={chartWrapperStyle}>
        <Chart
          options={options}
          series={[{ name: "Filtered", data: seriesData2 }]}
          type="bar"
          height="100%"
          width="100%"
        />
      </div>
    </div>
  );
};

export default D03HorizontalBarCharts;